/* IAH progressive shopping UI. All purchasing uses the native Shopify product form. */
(() => {
  const storage = (kind) => ({
    get(key, fallback) { try { return JSON.parse(window[kind].getItem(key)) ?? fallback; } catch { return fallback; } },
    set(key, value) { try { window[kind].setItem(key, JSON.stringify(value)); return true; } catch { return false; } },
  });
  const local = storage('localStorage');
  const session = storage('sessionStorage');
  const text = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };
  const currentPicker = (root) => [...root.querySelectorAll('variant-selects')].find(node => node.style.display !== 'none');
  const productURL = (value) => {
    try { const url = new URL(value, location.origin); return url.origin === location.origin && /\/products\/[^/]+$/.test(url.pathname) ? url : null; } catch { return null; }
  };
  function wireDialog(dialog, signal) {
    if (!dialog) return;
    dialog.addEventListener('click', event => {
      if (event.target.closest('[data-iah-close]') || event.target === dialog && (event.clientX < dialog.getBoundingClientRect().left || event.clientX > dialog.getBoundingClientRect().right || event.clientY < dialog.getBoundingClientRect().top || event.clientY > dialog.getBoundingClientRect().bottom)) dialog.close();
    }, { signal });
  }
  function openDialog(dialog, opener) {
    if (!dialog || dialog.open) return;
    dialog.addEventListener('close', () => { if (opener?.isConnected) opener.focus({ preventScroll:true }); }, { once:true });
    dialog.showModal();
  }

  class ShopNavigation extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.items = this.readItems();
      this.querySelectorAll('dialog').forEach(dialog => wireDialog(dialog, signal));
      this.querySelector('.iah-bottom-nav')?.removeAttribute('hidden');
      document.querySelectorAll('.iah-header-favorites').forEach(node => node.hidden = false);
      this.refresh();
      document.addEventListener('click', event => {
        const button = event.target.closest('[data-iah-favorite]');
        if (button) { event.preventDefault(); this.toggle(button); return; }
        const wishlist = event.target.closest('[data-iah-wishlist]');
        if (wishlist) { this.render(); openDialog(this.querySelector('#IAHFavorites'), wishlist); }
        const search = event.target.closest('[data-iah-search]');
        if (search) { event.preventDefault(); openDialog(this.querySelector('#IAHSearch'), search); }
        const cart = event.target.closest('[data-iah-cart]');
        if (cart && document.querySelector('cart-drawer')) { event.preventDefault(); document.querySelector('cart-drawer').open(cart); }
      }, { signal });
      window.addEventListener('storage', event => { if (event.key === 'iah:favorites:v1') { this.items = this.readItems(); this.refresh(); if (this.querySelector('#IAHFavorites')?.open) this.render(); } }, { signal });
      this.observer = new MutationObserver(records => {
        if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches?.('[data-iah-favorite],.cart-count-bubble') || node.querySelector?.('[data-iah-favorite],.cart-count-bubble'))))) this.scheduleRefresh();
        if (records.some(record => record.target.closest?.('#cart-icon-bubble'))) this.scheduleRefresh();
      });
      this.observer.observe(document.body, { childList:true, subtree:true, characterData:true });
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); clearTimeout(this.toastTimer); cancelAnimationFrame(this.frame); }
    readItems() {
      const items = local.get('iah:favorites:v1', []);
      return Array.isArray(items) ? items.filter(item => item && typeof item.handle === 'string' && typeof item.title === 'string' && productURL(item.url)).slice(0,100) : [];
    }
    scheduleRefresh() { if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = null; this.refresh(); }); }
    refresh() {
      document.querySelectorAll('[data-iah-favorite]').forEach(button => { button.hidden = false; button.setAttribute('aria-pressed', String(this.items.some(item => item.handle === button.dataset.handle))); });
      const count = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden]')?.textContent?.trim() || (document.querySelector('#cart-icon-bubble .cart-count-bubble') ? '99+' : '0');
      this.querySelectorAll('[data-iah-cart-count]').forEach(node => text(node, count));
    }
    toggle(button) {
      const index = this.items.findIndex(item => item.handle === button.dataset.handle);
      if (index >= 0) this.items.splice(index,1);
      else {
        if (!productURL(button.dataset.url)) return;
        this.items.unshift({ handle:button.dataset.handle, title:button.dataset.title, url:button.dataset.url, image:button.dataset.image });
        this.items = this.items.slice(0,100);
      }
      const saved = local.set('iah:favorites:v1', this.items);
      this.refresh(); this.toast(saved ? (index >= 0 ? this.dataset.unsaved : this.dataset.saved) : this.dataset.storageError);
    }
    toast(message) { const node = this.querySelector('.iah-toast'); text(node,message); node.hidden = false; clearTimeout(this.toastTimer); this.toastTimer = setTimeout(() => node.hidden = true,4500); }
    render() {
      const list = this.querySelector('[data-iah-favorites-list]');
      if (!list) return;
      list.replaceChildren();
      if (!this.items.length) { const p = document.createElement('p'); p.textContent = this.dataset.empty; list.append(p); }
      this.items.forEach(item => {
        const row = document.createElement('div'); row.className = 'iah-favorite-row';
        const photo = document.createElement('a'); const title = document.createElement('a');
        const root = this.dataset.root.replace(/\/$/,'');
        photo.href = title.href = `${root}/products/${encodeURIComponent(item.handle)}`;
        photo.tabIndex = -1; photo.setAttribute('aria-hidden','true'); title.textContent = item.title;
        if (typeof item.image === 'string' && /^(https:\/\/cdn\.shopify\.com\/|\/\/[^/]+\/cdn\/shop\/|\/cdn\/shop\/)/.test(item.image)) {
          const image = document.createElement('img'); image.src = item.image; image.alt = ''; image.loading = 'lazy'; photo.append(image);
        }
        const remove = document.createElement('button'); remove.type = 'button'; remove.append(this.querySelector('[data-iah-remove-icon]').content.cloneNode(true)); remove.setAttribute('aria-label', `${this.dataset.remove}: ${item.title}`);
        remove.addEventListener('click', () => { this.items = this.items.filter(other => other.handle !== item.handle); if (!local.set('iah:favorites:v1', this.items)) this.toast(this.dataset.storageError); this.refresh(); this.render(); list.querySelector('button')?.focus(); });
        row.append(photo,title,remove); list.append(row);
      });
    }
  }

  class ProductTools extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('product-info');
      if (!this.root?.id.startsWith('MainProduct-') || this.closest('quick-add-modal')) return;
      this.controller = new AbortController(); const { signal } = this.controller;
      this.dialog = this.querySelector('dialog'); this.bar = this.querySelector('.iah-product-bar');
      wireDialog(this.dialog, signal);
      this.nativeSubmit = this.root.querySelector(`#ProductSubmitButton-${CSS.escape(this.dataset.section)}`);
      if (!this.nativeSubmit) return;
      this.querySelector('[data-iah-options-open]')?.addEventListener('click', event => {
        const picker = currentPicker(this.root); if (!picker) return;
        this.placeholder = document.createComment('IAH native option picker position'); picker.before(this.placeholder);
        this.querySelector('[data-iah-options-slot]').append(picker);
        openDialog(this.dialog,event.currentTarget);
      }, { signal });
      this.dialog.addEventListener('close', () => {
        const slot = this.querySelector('[data-iah-options-slot]');
        if (this.placeholder?.isConnected) { [...slot.children].forEach(node => this.placeholder.before(node)); this.placeholder.remove(); }
      }, { signal });
      this.querySelector('[data-iah-bar-add]').addEventListener('click', () => {
        if (!this.nativeSubmit.disabled && this.nativeSubmit.getAttribute('aria-disabled') !== 'true') this.nativeSubmit.click();
      }, { signal });
      this.observer = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.scheduleSync();
      });
      this.observer.observe(this.root, { childList:true, subtree:true, attributes:true, characterData:true, attributeFilter:['disabled','aria-disabled','class','value'] });
      this.unsubscribe = typeof subscribe === 'function' ? subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
        if (event.source !== 'product-form' || String(event.productVariantId) !== this.root.querySelector('input[name="id"]')?.value) return;
        this.addedUntil = Date.now()+1800; this.sync(); clearTimeout(this.addedTimer); this.addedTimer = setTimeout(() => this.sync(),1850);
      }) : null;
      this.root.addEventListener('change', () => this.scheduleSync(), { signal });
      this.sync();
      this.mobile = matchMedia('(max-width:749px)');
      this.updateBar = () => {
        this.bar.hidden = !this.mobile.matches && this.nativeVisible;
        document.body.classList.toggle('iah-has-product-bar',!this.bar.hidden);
      };
      this.mobile.addEventListener('change',this.updateBar,{signal});
      this.visibilityObserver = new IntersectionObserver(entries => { this.nativeVisible = entries[0].isIntersecting; this.updateBar(); },{threshold:0});
      this.visibilityObserver.observe(this.nativeSubmit);
      this.updateBar();
    }
    disconnectedCallback() {
      this.dialog?.close(); this.controller?.abort(); this.observer?.disconnect(); this.visibilityObserver?.disconnect(); this.unsubscribe?.(); clearTimeout(this.addedTimer); cancelAnimationFrame(this.frame);
      if (!document.querySelector('iah-product-tools .iah-product-bar:not([hidden])')) document.body.classList.remove('iah-has-product-bar');
    }
    scheduleSync() { if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame=null; this.sync(); }); }
    sync() {
      if (!this.nativeSubmit?.isConnected) return;
      const priceRoot = this.root.querySelector(`[id="price-${this.dataset.section}"]`);
      const price = priceRoot?.querySelector('.price--on-sale .price-item--sale') || priceRoot?.querySelector('.price-item--regular');
      text(this.querySelector('[data-iah-bar-price]'), priceRoot?.classList.contains('hidden') ? '' : (price?.textContent.trim() || ''));
      const loading = this.nativeSubmit.classList.contains('loading');
      const button = this.querySelector('[data-iah-bar-add]');
      const disabled = this.nativeSubmit.disabled || this.nativeSubmit.getAttribute('aria-disabled') === 'true';
      if (button.disabled !== disabled) button.disabled = disabled;
      const label = loading ? this.dataset.loading : this.addedUntil > Date.now() ? this.dataset.added : this.nativeSubmit.querySelector('span')?.textContent.trim();
      text(button,label || window.variantStrings.addToCart);
      const picker = currentPicker(this.root);
      const selections = picker ? [...picker.querySelectorAll('input[type="radio"]:checked, select option:checked')].map(input => input.tagName === 'OPTION' ? input.textContent.trim() : input.value).join(' / ') : '';
      const optionsButton = this.querySelector('[data-iah-options-open]');
      text(optionsButton,selections || this.dataset.optionsLabel);
      if (optionsButton) optionsButton.hidden = !picker;
    }
  }

  class SizeGuide extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController(); const { signal } = this.controller;
      this.dialog = this.querySelector('dialog'); wireDialog(this.dialog,signal);
      // Product description may occur after this block in the parser/editor.
      requestAnimationFrame(() => { if (this.isConnected) this.setup(signal); });
    }
    disconnectedCallback() { this.dialog?.close(); this.controller?.abort(); }
    setup(signal) {
      const explicit = this.querySelector('template')?.content;
      const root = this.closest('product-info');
      const table = explicit?.querySelector('table') || [...(root?.querySelectorAll('.product__description table') || [])].find(node => /chest|borst|sleeve|mouw|waist|taille|length|lengte/i.test(node.textContent) && /\bsize\b|\bmaten\b|\bmaat\b/i.test(node.textContent));
      if (!explicit && !table) return;
      const output = this.querySelector('[data-iah-guide-content]');
      output.replaceChildren();
      if (explicit) output.append(explicit.cloneNode(true));
      else {
        const clean = document.createElement('table');
        [...table.rows].forEach((row,index) => { const tr = clean.insertRow(); [...row.cells].forEach(cell => { const next = document.createElement(index === 0 || cell.cellIndex === 0 ? 'th' : 'td'); if (next.tagName === 'TH') next.scope = index === 0 ? 'col' : 'row'; next.textContent = cell.textContent.trim(); tr.append(next); }); });
        output.append(clean);
      }
      const rendered = output.querySelector('table');
      if (rendered) {
        this.querySelector('[data-iah-measuring]').hidden = !/chest|borst/i.test(rendered.textContent) || !/length|lengte/i.test(rendered.textContent);
        this.setupComparison(rendered);
      }
      // Triggers in the size label and the Size & fit row are re-rendered on variant change, so they are shown via a class and handled by delegation.
      const inline = root?.querySelector('[data-iah-guide-inline]');
      root?.classList.add('iah-has-size-guide');
      root?.addEventListener('click', event => { const trigger = event.target.closest('[data-iah-guide-inline]'); if (trigger) openDialog(this.dialog,trigger); }, { signal });
      if (!inline) {
        this.querySelector('[data-iah-guide-open]').hidden = false;
        this.querySelector('[data-iah-guide-open]').addEventListener('click', event => openDialog(this.dialog,event.currentTarget), { signal });
      }
    }
    setupComparison(table) {
      const size = /^(?:[2-6]?X{0,3}[SML]|[0-9]{1,3}(?:[-/]\d{1,3})?)$/i;
      const horizontal = [...(table.rows[0]?.cells || [])].slice(1).filter(cell => size.test(cell.textContent.trim())).length >= 2;
      const entries = horizontal ? [...table.rows[0].cells].slice(1).map((cell,i) => [i+1,cell.textContent.trim()]) : [...table.rows].slice(1).map((row,i) => [i+1,row.cells[0]?.textContent.trim()]);
      const valid = entries.filter(([,label]) => size.test(label || ''));
      if (valid.length < 2) return;
      const area = this.querySelector('[data-iah-compare]'); area.replaceChildren(); area.setAttribute('role','group'); area.setAttribute('aria-label',this.dataset.compare);
      this.querySelector('[data-iah-compare-help]').hidden = false;
      let selected = [];
      valid.forEach(([index,label]) => {
        const button = document.createElement('button'); button.type='button'; button.textContent=label; button.setAttribute('aria-pressed','false'); button.dataset.index=index;
        button.addEventListener('click', () => {
          selected = selected.includes(index) ? selected.filter(value => value !== index) : [...selected.slice(-1),index];
          area.querySelectorAll('button').forEach(node => node.setAttribute('aria-pressed',String(selected.includes(Number(node.dataset.index)))));
          [...table.rows].forEach((row,rowIndex) => {
            row.hidden = !horizontal && selected.length === 2 && rowIndex > 0 && !selected.includes(rowIndex);
            [...row.cells].forEach((cell,colIndex) => {
              cell.classList.toggle('iah-size-highlight',selected.includes(horizontal ? colIndex : rowIndex));
              cell.hidden = horizontal && selected.length === 2 && colIndex > 0 && !selected.includes(colIndex);
            });
          });
        });
        area.append(button);
      });
    }
  }

  if (!customElements.get('iah-shop-navigation')) customElements.define('iah-shop-navigation',ShopNavigation);
  if (!customElements.get('iah-product-tools')) customElements.define('iah-product-tools',ProductTools);
  if (!customElements.get('iah-size-guide')) customElements.define('iah-size-guide',SizeGuide);

  // Native filter history keeps the query. This adds product position and a remembered variant.
  const key = () => `iah:position:${location.pathname}${location.search}`;
  let restoring = false;
  function rememberPosition() {
    if (restoring || !document.querySelector('#product-grid')) return;
    session.set(key(),{ y:window.scrollY, at:Date.now() });
  }
  document.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    if (!link) return;
    const url = productURL(link.href);
    if (!url) return;
    rememberPosition();
    if (!url.searchParams.has('variant')) {
      const variant = session.get(`iah:variant:${url.pathname}`,null);
      if (variant && Date.now()-variant.at < 43200000 && /^\d+$/.test(variant.id)) { url.searchParams.set('variant',variant.id); link.href=url.href; }
    }
  },true);
  window.addEventListener('pagehide',() => {
    rememberPosition();
    const variant = new URLSearchParams(location.search).get('variant');
    if (variant && document.querySelector('product-info[id^="MainProduct-"]')) session.set(`iah:variant:${location.pathname}`,{id:variant,at:Date.now()});
  });
  window.addEventListener('pageshow',event => {
    if (event.persisted || performance.getEntriesByType('navigation')[0]?.type !== 'back_forward' || !document.querySelector('#product-grid')) return;
    const position = session.get(key(),null);
    if (!position || Date.now()-position.at > 43200000) return;
    restoring=true;
    requestAnimationFrame(() => requestAnimationFrame(() => { window.scrollTo({top:position.y,behavior:'instant'}); restoring=false; }));
  });
})();
